import { useEffect, useRef, useState } from 'react'
import { ChevronDown, ChevronUp, Link2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { OrderbookCompression } from './orderbookCompression'

interface OrderbookCompressionControlProps {
  value: OrderbookCompression
  onChange: (value: OrderbookCompression) => void
  label: string
  inherited?: boolean
  onReset?: () => void
}

export function OrderbookCompressionControl({
  value, onChange, label, inherited = false, onReset,
}: OrderbookCompressionControlProps) {
  const [input, setInput] = useState(value.value === null ? '' : String(value.value))
  const focused = useRef(false)
  const isMultiplier = value.mode === 'multiplier'
  const summary = isMultiplier ? `${value.value}×` : value.value === null ? 'All' : `±${value.value}%`
  const presets = isMultiplier ? [1, 10, 100, 1000, 10000] : [0.1, 0.5, 1, 5, 10]

  useEffect(() => {
    if (!focused.current) setInput(value.value === null ? '' : String(value.value))
  }, [value.mode, value.value])

  const commit = (next: number | null) => {
    if (isMultiplier) {
      if (next === null || !Number.isFinite(next) || next < 1) return
      onChange({ mode: 'multiplier', value: next })
    } else {
      if (next !== null && (!Number.isFinite(next) || next < 0)) return
      onChange({ mode: 'percent', value: next })
    }
  }

  const changeInput = (raw: string) => {
    setInput(raw)
    commit(raw.trim() ? Number(raw) : null)
  }

  const blurInput = () => {
    focused.current = false
    const parsed = input.trim() ? Number(input) : null
    const valid = parsed === null ? !isMultiplier : Number.isFinite(parsed) && parsed >= (isMultiplier ? 1 : 0)
    setInput(valid ? parsed === null ? '' : String(parsed) : value.value === null ? '' : String(value.value))
  }

  const increment = (direction: -1 | 1) => {
    const next = Math.max(isMultiplier ? 1 : 0, Number(((value.value ?? 0) + direction * (isMultiplier ? 1 : 0.1)).toPrecision(12)))
    setInput(String(next))
    commit(next)
  }

  const selectMode = (mode: OrderbookCompression['mode']) => {
    if (mode === value.mode) return
    focused.current = false
    setInput(mode === 'multiplier' ? '1' : '')
    onChange(mode === 'multiplier' ? { mode, value: 1 } : { mode, value: null })
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          aria-label={`${label} compression`}
          title={`${label}: ${summary}${onReset ? inherited ? ' · Shared setting' : ' · Custom setting' : ''}`}
          className={cn('h-7 min-w-20 gap-1.5 rounded px-2 text-[11px] tabular-nums',
            onReset && !inherited && 'border-primary/40 bg-primary/10 text-primary hover:bg-primary/15')}
        >
          {onReset && inherited && <Link2 className="h-3 w-3 text-muted-foreground" />}
          <span>{summary}</span>
          <ChevronDown className="ml-auto h-3 w-3 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={8} className="max-h-[var(--radix-popover-content-available-height)] w-56 overflow-y-auto p-0" aria-label={`${label} compression settings`}>
        <div className="border-b border-border/60 px-3 py-2">
          <div className="text-xs font-semibold">Compression</div>
          <div className="mt-0.5 text-[10px] text-muted-foreground">{label}{onReset ? inherited ? ' · Shared' : ' · Custom' : ' · Default'}</div>
        </div>
        <div className="space-y-2 p-2">
          <div className="flex overflow-hidden rounded border border-input bg-background focus-within:ring-1 focus-within:ring-ring">
            <Input
              type="number"
              min={isMultiplier ? 1 : 0}
              step="any"
              inputMode="decimal"
              value={input}
              onFocus={() => { focused.current = true }}
              onChange={(event) => changeInput(event.target.value)}
              onBlur={blurInput}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.currentTarget.blur()
                if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
                  event.preventDefault()
                  increment(event.key === 'ArrowUp' ? 1 : -1)
                }
              }}
              placeholder={isMultiplier ? '1' : 'All levels'}
              aria-label={`${label} compression ${isMultiplier ? 'multiplier' : 'percentage'}`}
              className="h-8 min-w-0 flex-1 rounded-none border-0 bg-transparent text-xs tabular-nums shadow-none focus-visible:ring-0 [&::-webkit-inner-spin-button]:appearance-none [appearance:textfield]"
            />
            <span className="self-center px-2 text-xs text-muted-foreground">{isMultiplier ? '×' : '%'}</span>
            <div className="flex w-6 flex-col border-l border-border/60">
              <button type="button" className="flex flex-1 items-center justify-center hover:bg-accent" onClick={() => increment(1)} aria-label={`Increase ${label} compression`}><ChevronUp className="h-3 w-3" /></button>
              <button type="button" className="flex flex-1 items-center justify-center border-t border-border/60 hover:bg-accent" onClick={() => increment(-1)} aria-label={`Decrease ${label} compression`}><ChevronDown className="h-3 w-3" /></button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-0.5 rounded bg-muted/60 p-0.5" role="group" aria-label="Compression mode">
            {(['multiplier', 'percent'] as const).map((mode) => (
              <button key={mode} type="button" aria-pressed={value.mode === mode} onClick={() => selectMode(mode)}
                className={cn('rounded py-1 text-xs font-semibold transition-colors', value.mode === mode ? 'bg-primary/15 text-primary shadow-sm' : 'text-muted-foreground hover:bg-accent')}
                aria-label={mode === 'multiplier' ? 'Tick multiplier' : 'Percentage range'}
              >{mode === 'multiplier' ? '×' : '%'}</button>
            ))}
          </div>
          <div className="space-y-0.5">
            {presets.map((preset) => (
              <button key={preset} type="button" onClick={() => { setInput(String(preset)); commit(preset) }}
                className={cn('flex w-full items-center justify-between rounded px-2 py-1 text-left text-[11px] tabular-nums hover:bg-accent', value.value === preset && 'bg-primary/10 text-primary')}
                aria-label={`Set ${label} compression to ${preset}${isMultiplier ? '×' : '%'}`}
              ><span>{preset.toLocaleString()}{isMultiplier ? '×' : '%'}</span>{value.value === preset && <span className="h-1 w-1 rounded-full bg-primary" />}</button>
            ))}
          </div>
          <p className="px-1 text-[10px] leading-relaxed text-muted-foreground">
            {isMultiplier ? 'Group price levels by tick size × multiplier. Enter any value ≥ 1.' : 'Show bids and asks within this % of price. Clear to show all levels.'}
          </p>
        </div>
        {onReset && (
          <div className="border-t border-border/60 p-2">
            <Button variant="ghost" disabled={inherited} onClick={onReset} className="h-7 w-full justify-start gap-2 px-2 text-[11px]">
              <Link2 className="h-3 w-3" />{inherited ? 'Using shared setting' : 'Use shared setting'}
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
